import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, PageHeader, Toggle, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { PermissionGate } from "@/components/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useContentTemplate,
  useContentTemplates,
} from "@/hooks/useContentTemplates";
import { useContentTestRun } from "@/hooks/useContentTestRun";
import type {
  InputField,
  TemplateFull,
  TemplateSummary,
} from "@/types/content";

type InputsState = Record<string, string | number>;

const fieldLabel = (field: InputField) => field.label || field.name;

const buildInitialInputs = (schema: InputField[]): InputsState => {
  const next: InputsState = {};
  for (const field of schema) {
    next[field.name] = field.type === "number" ? "" : "";
  }
  return next;
};

const valueAsString = (value: string | number | undefined): string => {
  if (value === undefined || value === null) return "";
  return typeof value === "number" ? String(value) : value;
};

const allRequiredFilled = (
  schema: InputField[],
  inputs: InputsState
): boolean => {
  for (const field of schema) {
    if (!field.required) continue;
    const raw = inputs[field.name];
    if (raw === undefined || raw === null) return false;
    const asString = typeof raw === "number" ? String(raw) : raw;
    if (!asString.trim()) return false;
  }
  return true;
};

const inputsForSubmit = (
  schema: InputField[],
  inputs: InputsState
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const field of schema) {
    const raw = inputs[field.name];
    if (raw === undefined || raw === null || raw === "") continue;
    if (field.type === "number") {
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isNaN(num)) out[field.name] = num;
    } else {
      out[field.name] = typeof raw === "number" ? String(raw) : raw;
    }
  }
  return out;
};

interface TemplateComboboxProps {
  templates: TemplateSummary[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

const TemplateCombobox = ({
  templates,
  loading,
  selectedId,
  onSelect,
}: TemplateComboboxProps) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((tpl) => {
      return (
        tpl.title.toLowerCase().includes(q) ||
        tpl.slug.toLowerCase().includes(q) ||
        tpl.content_type.toLowerCase().includes(q) ||
        tpl.locale.toLowerCase().includes(q)
      );
    });
  }, [templates, query]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <div className="fg">
      <label className="fl">Template</label>
      <div className="cbx">
        <div className="srch" style={{ maxWidth: "none" }}>
          <Icon name="search" />
          <input
            className="inp"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 140)}
            placeholder={
              selected
                ? `${selected.title} (${selected.slug})`
                : loading
                ? "Loading templates..."
                : "Search by title, slug, type, or locale"
            }
          />
        </div>
        {open && filtered.length > 0 && (
          <div className="cbx-list">
            {filtered.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                className={`cbx-i ${tpl.id === selectedId ? "on" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(tpl.id);
                  setQuery("");
                  setOpen(false);
                }}
              >
                <span className="tx">
                  <b>{tpl.title}</b>
                  <span>
                    {tpl.content_type} · {tpl.locale} · {tpl.audience_role} ·{" "}
                    {tpl.slug}
                  </span>
                </span>
                <Badge variant={tpl.status === "active" ? "success" : "default"}>
                  {tpl.status}
                </Badge>
              </button>
            ))}
          </div>
        )}
        {open && !filtered.length && !loading && (
          <div className="cbx-list">
            <div style={{ padding: "8px 9px", fontSize: 12.5, color: "var(--ink-4)" }}>
              No matching templates.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface DynamicInputFormProps {
  template: TemplateFull;
  inputs: InputsState;
  onChange: (name: string, value: string | number) => void;
}

const DynamicInputForm = ({
  template,
  inputs,
  onChange,
}: DynamicInputFormProps) => {
  if (!template.input_schema.length) {
    return (
      <p className="meta">This template has no declared inputs.</p>
    );
  }
  return (
    <>
      {template.input_schema.map((field) => {
        const value = inputs[field.name];
        const stringValue = valueAsString(value);
        const overLimit =
          typeof field.max_len === "number" && stringValue.length > field.max_len;

        return (
          <div key={field.name} className="fg">
            <div className="lbl-row">
              <label className="fl">
                {fieldLabel(field)}
                {field.required && <span className="req">*</span>}
              </label>
              {typeof field.max_len === "number" && (
                <span className={`cnt ${overLimit ? "warn" : ""}`}>
                  {stringValue.length} / {field.max_len}
                </span>
              )}
            </div>

            {field.type === "text" ? (
              <textarea
                className="ta"
                value={stringValue}
                onChange={(e) => onChange(field.name, e.target.value)}
                rows={4}
                maxLength={field.max_len ?? undefined}
              />
            ) : field.type === "number" ? (
              <input
                className="inp num"
                type="number"
                value={stringValue}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            ) : field.type === "enum" ? (
              <select
                className="sel"
                value={stringValue}
                onChange={(e) => onChange(field.name, e.target.value)}
              >
                <option value="">— Select —</option>
                {(field.enum ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="inp"
                type="text"
                value={stringValue}
                onChange={(e) => onChange(field.name, e.target.value)}
                maxLength={field.max_len ?? undefined}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

const ContentTestRun = () => {
  const { can } = usePermissions();
  const canTest = can("content_generation", "test");

  const { data: listResp, isLoading: loadingList } = useContentTemplates({
    status: "active",
  });
  const templates = listResp?.items ?? [];

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: selectedTemplate, isLoading: loadingTemplate } =
    useContentTemplate(selectedId ?? "new");

  const [inputs, setInputs] = useState<InputsState>({});
  const [includeAudio, setIncludeAudio] = useState(false);
  const [showCitations, setShowCitations] = useState(false);
  const [copied, setCopied] = useState(false);

  const runMutation = useContentTestRun();

  // Reset form whenever the selected template changes.
  useEffect(() => {
    if (selectedTemplate) {
      setInputs(buildInitialInputs(selectedTemplate.input_schema));
      setIncludeAudio(false);
      setShowCitations(false);
      runMutation.reset();
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id]);

  const audioAllowed = selectedTemplate?.content_type === "voice_message";
  const requiredFilled = selectedTemplate
    ? allRequiredFilled(selectedTemplate.input_schema, inputs)
    : false;

  const handleChange = (name: string, value: string | number) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = () => {
    if (!selectedTemplate || !requiredFilled || !canTest) return;
    runMutation.mutate({
      template_id: selectedTemplate.id,
      inputs: inputsForSubmit(selectedTemplate.input_schema, inputs),
      include_audio: audioAllowed && includeAudio,
    });
    setCopied(false);
    setShowCitations(false);
  };

  const handleClear = () => {
    if (!selectedTemplate) return;
    setInputs(buildInitialInputs(selectedTemplate.input_schema));
    setIncludeAudio(false);
    runMutation.reset();
    setCopied(false);
    setShowCitations(false);
  };

  const handleCopy = async () => {
    const text = runMutation.data?.text;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can be denied by browser permissions; swallow silently.
    }
  };

  const result = runMutation.data;
  const errorMessage =
    runMutation.error instanceof Error
      ? runMutation.error.message
      : runMutation.error
      ? "Generation failed."
      : null;

  return (
    <div className="page wide">
      <PageHeader
        label="Content Generation"
        title="Content Test Run"
        description="Run a template against sample inputs and see what gets generated."
      />

      <PermissionGate resource="content_generation" action="view">
        <div className="md c">
          {/* Left — input panel */}
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-h tight">
              <div>
                <h2>Inputs</h2>
              </div>
            </div>
            <div className="card-b">
              <TemplateCombobox
                templates={templates}
                loading={loadingList}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />

              {selectedTemplate && (
                <div className="chips" style={{ margin: "16px 0" }}>
                  <span className="chip on">{selectedTemplate.content_type}</span>
                  <span className="chip">{selectedTemplate.locale}</span>
                  <span className="chip">{selectedTemplate.audience_role}</span>
                  <span
                    className="chip"
                    style={{ fontFamily: "var(--mono)" }}
                  >
                    {selectedTemplate.slug}
                  </span>
                </div>
              )}

              {loadingTemplate && selectedId !== null && (
                <p className="meta">Loading template...</p>
              )}

              {selectedTemplate && (
                <>
                  <div className="divider" />

                  <DynamicInputForm
                    template={selectedTemplate}
                    inputs={inputs}
                    onChange={handleChange}
                  />

                  <div className="divider" />

                  <div className="tg-row" style={{ paddingTop: 0 }}>
                    <span className="tx">
                      <b>Include audio (voice_message only)</b>
                    </span>
                    <Toggle
                      checked={includeAudio && audioAllowed}
                      onChange={setIncludeAudio}
                      disabled={!audioAllowed}
                    />
                  </div>
                </>
              )}

              {!selectedTemplate && !loadingTemplate && (
                <div className="empty sm" style={{ padding: "32px 0" }}>
                  <p>Pick a template above to render its input form.</p>
                </div>
              )}
            </div>

            {selectedTemplate && (
              <div className="card-f">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClear}
                  disabled={runMutation.isPending}
                >
                  Clear
                </Button>
                <div className="r">
                  {!canTest && (
                    <span
                      className="meta"
                      style={{ color: "var(--warn)", alignSelf: "center" }}
                    >
                      You don't have permission to run test generations.
                    </span>
                  )}
                  <Button
                    type="button"
                    onClick={handleGenerate}
                    loading={runMutation.isPending}
                    disabled={
                      !requiredFilled || !canTest || runMutation.isPending
                    }
                  >
                    <Icon name="play" />
                    Generate
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right — output panel */}
          <div className="card">
            <div className="card-h">
              <div>
                <h2>Output</h2>
                {selectedTemplate ? (
                  <p>
                    {selectedTemplate.content_type} · {selectedTemplate.locale} ·{" "}
                    {selectedTemplate.audience_role}
                    {result?.text && (
                      <>
                        {" · "}
                        <span className="mono">
                          {/* The hook returns generation metadata; chat_model lives on the log, not here.
                              We surface the template's configured model as a near-equivalent until the
                              generate response carries it explicitly. */}
                          {selectedTemplate.model_overrides?.chat_model ??
                            "gpt-4o-mini"}
                        </span>
                      </>
                    )}
                  </p>
                ) : (
                  <p>Awaiting template selection.</p>
                )}
              </div>
              {result?.text && (
                <div className="r">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleCopy}
                  >
                    <Icon name="copy" />
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              )}
            </div>

            <div className="card-b">
              {runMutation.isPending && (
                <div
                  className="meta"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "32px 0",
                  }}
                >
                  <svg
                    className="animate-spin"
                    style={{ width: 16, height: 16, color: "var(--g)" }}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Generating...
                </div>
              )}

              {!runMutation.isPending && errorMessage && (
                <div className="al e">
                  <Icon name="alert" />
                  <div className="cnt-x">{errorMessage}</div>
                </div>
              )}

              {!runMutation.isPending && result && (
                <>
                  <pre
                    className="code"
                    style={{
                      fontSize: 13,
                      lineHeight: 1.7,
                      padding: "14px 16px",
                      fontFamily: "var(--sans)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {result.text}
                  </pre>

                  {result.audio_url && (
                    <audio
                      controls
                      src={result.audio_url}
                      style={{ width: "100%", marginTop: 12 }}
                    />
                  )}

                  {result.citations.length > 0 && (
                    <div className="sec">
                      <div
                        className="sec-h"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <b
                          style={{
                            fontSize: 11,
                            letterSpacing: ".08em",
                            textTransform: "uppercase",
                            color: "var(--ink-3)",
                          }}
                        >
                          Citations ({result.citations.length})
                        </b>
                        <button
                          type="button"
                          className="act-txt"
                          style={{ marginLeft: "auto" }}
                          onClick={() => setShowCitations((v) => !v)}
                        >
                          {showCitations ? "Hide" : "Show"}
                        </button>
                      </div>
                      {showCitations &&
                        result.citations.map((citation, idx) => (
                          <div
                            key={`${citation.document_id ?? "anon"}-${idx}`}
                            className="cit"
                          >
                            <div className="cit-h">
                              <b>
                                {citation.source ??
                                  citation.document_id ??
                                  "Source"}
                              </b>
                              {typeof citation.score === "number" && (
                                <span className="sc">
                                  {citation.score.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {citation.snippet && <p>{citation.snippet}</p>}
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}

              {!runMutation.isPending && !result && !errorMessage && (
                <div className="empty sm" style={{ padding: "48px 0" }}>
                  <p>Run the template to see the generated output here.</p>
                </div>
              )}
            </div>

            {!runMutation.isPending && result && (
              <div className="card-f">
                <span className="meta">
                  Generated in{" "}
                  <b className="num" style={{ color: "var(--ink-2)" }}>
                    {result.latency_ms}ms
                  </b>{" "}
                  ·{" "}
                  <b className="num" style={{ color: "var(--ink-2)" }}>
                    {result.tokens.prompt}
                  </b>
                  +
                  <b className="num" style={{ color: "var(--ink-2)" }}>
                    {result.tokens.output}
                  </b>{" "}
                  tokens · log{" "}
                  <Link
                    to={`/generation-logs/${result.log_id}`}
                    className="mono"
                    style={{ color: "var(--g-d)", fontWeight: 600 }}
                  >
                    #{result.log_id}
                  </Link>
                </span>
              </div>
            )}
          </div>
        </div>
      </PermissionGate>
    </div>
  );
};

export default ContentTestRun;
